import * as React from "react";
import * as ReactDOM from "react-dom";
import Inputmask from "inputmask";
import * as utils from "../../utils/utils";
import { InputValueType } from "../../enumerations";
import { InputSize, InputMask } from "./";

export interface IInputBaseProperties
{
    /**
     * Wenn angegeben, kann hiermit das DOM-Element dieser Komponente ermittelt werden.
     */
    inputRef?: (elem: HTMLInputElement | HTMLTextAreaElement) => void;

    /**
     * Ein Wert zum indentifizieren dieser Komponente. Dieser Wert muss global eindeutig sein.
     */
    id?: string;

    /**
     * Der Name dieser Komponente.
     */
    name?: string;

    /**
     * Leerzeilen separierte CSS-Klassen. Wird an die schon vorhanden Klassen angehängt.
     * Kann dazu benutzt werden um Änderungen am Standard Style vorzunehmen.
     */
    className?: string;

    /**
     * Ein platzhalter Text der angezeigt wird, wenn das Input keine Wert aufweist.
     */
    placeholder?: string;

    /**
     * Die Meldung die bei einem Fehler angezeigt werden soll.
     */
    errorMessage?: string;

    /**
     * Die Größe des Inputs.
     */
    size?: InputSize;

    /**
     * Der Wert des Inputs.
     */
    value?: string;

    /**
     * Der Datentyp des Inputs.
     */
    valueType?: InputValueType;

    /**
     * Eine Maske für die Darstellung von bestimmten Formatierungen wie Datum oder Uhrzeit.
     */
    mask?: InputMask;

    /**
     * Ein Wert der angibt, ob die Rechtschreibprüfung des Browsers aktiviert ist.
     */
    spellCheck?: boolean;

    /**
     * Ein Wert der angibt, ob das Input fokussiert werden soll.
     */
    autoFocus?: boolean;

    /**
     * TextArea Zeilen.
     */
    rows?: number;

    /**
     * Ein Wert der angibt, ob das Input abgeschaltet ist.
     */
    isDisabled?: boolean;

    /**
     * Ein Wert der angibt, ob der Wert des Inputs nicht bearbeitet werden darf.
     */
    isReadonly?: boolean;

    /**
     * Ein Wert der angibt, ob das Input eine TextArea ist.
     */
    isTextArea?: boolean;

    /**
     * Ein Wert der angibt, ob das Input eines für Passwörter ist.
     */
    isPassword?: boolean;

    /**
     * Tab Reihenfolge.
     */
    tabIndex?: number;

    /**
     * Wird ausgeführt, wenn sich der Wert der TextBox ändert.
     */
    onTextChange?: (value: any[], originalValue: string[], e: Event) => void;

    /**
     * Wird ausgeführt, sobald eine Taste von Tastatur losgelassen wird.
     */
    onKeyUp?: (e: KeyboardEvent) => void;

    /**
     * Wird ausgeführt, sobald das Input den Fokus erhält.
     */
    onFocus?: () => void;

    /**
     * Wird ausgeführt, sobald das Input den Fokus verliert.
     */
    onBlur?: () => void;
}

export class InputBase extends React.Component<IInputBaseProperties>
{
    /**
     * Standardwerte setzen.
     */
    public static defaultProps: Partial<IInputBaseProperties> = {
        size: InputSize.normal,
        valueType: InputValueType.string,
        spellCheck: false,
        rows: 6
    };

    /**
     * Initialisiert die Komponente @see InputBase.
     * @param props Die Eigenschaften dieser Komponente.
     */
    constructor(props: IInputBaseProperties)
    {
        super(props);
    }

    /**
     * Wird ausgeführt, sobald sich der Wert des Inputs ändert.
     * @param e Das Ereignis welches ausgelöst wurde.
     */
    private onInputChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    {
        const { isTextArea, valueType, onTextChange } = this.props;

        // Typ des Inputs ermitteln.
        let type: string = e.target.type;

        // Wert des Inputs ermitteln.
        let value = utils.getValue<any>(type, valueType, e.target as HTMLElement);

        // Falls eine "onChange" Funktion übergeben wurde, diese hier ausführen.
        onTextChange && onTextChange(value.value, value.originalValue, e.nativeEvent);
    }

    /**
     * Wird ausgeführt, sobald sich der Wert des Inputs ändert.
     * @param e Das Ereignis welches ausgelöst wurde.
     */
    private onTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) =>
    {
        const { isTextArea, valueType, onTextChange } = this.props;

        let value = (e.target as HTMLTextAreaElement).value;
        onTextChange && onTextChange([value], [value], e.nativeEvent);
    }

    /**
     * Wird ausgeführt, sobald eine Taste losgelassen wird.
     * @param e Das Keyboard Ereignis welches ausgelöst wurde.
     */
    private onKeyUp = (e: React.KeyboardEvent<HTMLElement>) =>
    {
        this.props.onKeyUp && this.props.onKeyUp(e.nativeEvent);
    }

    /**
     * Wird bei jedem Rendern der Komponente ausgeführt und richtet die
     * Masken (falls vorhanden) ein.
     * @param ref Das DOM-Element dieser Komponente.
     */
    private setInputMask = (ref: HTMLElement) =>
    {
        if (ref != null && this.props.mask !== InputMask.none)
        {
            Inputmask().remove(ref);
            Inputmask({
                showMaskOnHover: false
            }).mask(ref);
        }

        if (this.props.inputRef != null)
            this.props.inputRef(ref as HTMLInputElement);
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        const {
            inputRef,
            size,
            className,
            tabIndex,
            id,
            name,
            placeholder,
            errorMessage,
            mask,
            value,
            spellCheck,
            autoFocus,
            rows,
            isDisabled,
            isReadonly,
            isTextArea,
            isPassword,
            onFocus,
            onBlur
        } = this.props;

        var classNames: string[] = ["input2"]

        if (size === InputSize.small)
            classNames.push("input--small");
        else if (size === InputSize.normal)
            classNames.push("input--normal");

        if (className != null)
            classNames.push(className);

        var type: string = "text";
        if (mask === InputMask.number)
            type = "number";
        else if (isPassword)
            type = "password";

        if (isTextArea)
        {
            return (
                <textarea
                    ref={inputRef}
                    tabIndex={tabIndex}
                    id={id}
                    name={name}
                    className={classNames.join(" ")}
                    placeholder={placeholder}
                    title={errorMessage}
                    value={value != null ? value : ""}
                    spellCheck={spellCheck}
                    autoFocus={autoFocus}
                    rows={rows}
                    disabled={isDisabled}
                    readOnly={isReadonly}
                    onChange={this.onTextAreaChange}
                    onKeyUp={this.onKeyUp}
                    onFocus={onFocus}
                    onBlur={onBlur} />
            );
        }
        else
        {
            return (
                <input
                    ref={this.setInputMask}
                    tabIndex={tabIndex}
                    id={id}
                    name={name}
                    className={classNames.join(" ")}
                    placeholder={placeholder}
                    title={errorMessage}
                    data-inputmask-alias={mask}
                    data-inputmask-rightalign={(mask === InputMask.number) ? "false" : ""}
                    type={type}
                    value={value != null ? value : ""}
                    spellCheck={spellCheck}
                    autoFocus={autoFocus}
                    disabled={isDisabled}
                    readOnly={isReadonly}
                    onChange={this.onInputChange}
                    onKeyUp={this.onKeyUp}
                    onFocus={onFocus}
                    onBlur={onBlur} />
            );
        }
    }
}